import { useState, useEffect } from "react";
import { MapContainer, TileLayer, GeoJSON, useMap } from "react-leaflet";
import L from "leaflet";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Globe, ArrowRight, Ship, TrendingUp, Package } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer } from "recharts";
import "leaflet/dist/leaflet.css";

const TRADE_FLOWS = [
  { from: "Brazil", to: "China", commodity: "Soybean", volume: 85.2, value: 42.6, change: 5.2 },
  { from: "USA", to: "Japan", commodity: "Wheat", volume: 12.8, value: 8.1, change: -2.1 },
  { from: "Australia", to: "India", commodity: "Cotton", volume: 3.2, value: 4.8, change: 8.5 },
  { from: "Ghana", to: "Netherlands", commodity: "Cocoa", volume: 0.45, value: 2.1, change: 12.3 },
  { from: "Ivory Coast", to: "USA", commodity: "Cocoa", volume: 0.38, value: 1.8, change: 9.7 },
  { from: "Vietnam", to: "Germany", commodity: "Coffee", volume: 0.92, value: 3.2, change: 3.4 },
  { from: "Colombia", to: "USA", commodity: "Coffee", volume: 0.65, value: 2.4, change: 1.2 },
  { from: "Indonesia", to: "India", commodity: "Palm Oil", volume: 8.5, value: 7.2, change: -1.8 },
  { from: "Russia", to: "Egypt", commodity: "Wheat", volume: 11.2, value: 5.6, change: 4.5 },
  { from: "Argentina", to: "China", commodity: "Maize", volume: 7.8, value: 2.9, change: 6.8 },
  { from: "Saudi Arabia", to: "Japan", commodity: "Crude Oil", volume: 125, value: 82.5, change: -3.2 },
  { from: "Chile", to: "China", commodity: "Copper", volume: 2.8, value: 18.5, change: 7.9 },
];

const TOP_EXPORTERS = [
  { country: "Brazil", value: 89.5, commodities: ["Soybean", "Coffee", "Sugar"] },
  { country: "USA", value: 72.3, commodities: ["Wheat", "Maize", "Cotton"] },
  { country: "Russia", value: 45.2, commodities: ["Wheat", "Crude Oil", "Natural Gas"] },
  { country: "Australia", value: 38.7, commodities: ["Coal", "Iron Ore", "Wheat"] },
  { country: "Indonesia", value: 32.1, commodities: ["Palm Oil", "Coal", "Rubber"] },
];

const TOP_IMPORTERS = [
  { country: "China", value: 185.3, commodities: ["Soybean", "Crude Oil", "Copper"] },
  { country: "Japan", value: 68.4, commodities: ["Crude Oil", "Natural Gas", "Wheat"] },
  { country: "India", value: 52.8, commodities: ["Crude Oil", "Palm Oil", "Gold"] },
  { country: "Germany", value: 41.2, commodities: ["Natural Gas", "Coffee", "Cocoa"] },
  { country: "USA", value: 38.5, commodities: ["Coffee", "Cocoa", "Crude Oil"] },
];

const COMMODITY_VOLUMES = [
  { name: "Crude Oil", volume: 4800 },
  { name: "Natural Gas", volume: 1200 },
  { name: "Soybean", volume: 380 },
  { name: "Wheat", volume: 210 },
  { name: "Maize", volume: 180 },
  { name: "Coffee", volume: 12 },
  { name: "Cocoa", volume: 5 },
  { name: "Copper", volume: 25 },
];

export default function TradeFlowPage() {
  const [selectedCommodity, setSelectedCommodity] = useState("all");

  const filteredFlows = selectedCommodity === "all" 
    ? TRADE_FLOWS 
    : TRADE_FLOWS.filter(f => f.commodity === selectedCommodity);

  const commodities = [...new Set(TRADE_FLOWS.map(f => f.commodity))];

  return (
    <div data-testid="trade-flow-page" className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-wider text-zinc-100">
            Trade Flow
          </h1>
          <p className="text-xs text-zinc-500 font-mono mt-1">
            Global commodity trade flows and market corridors
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedCommodity} onValueChange={setSelectedCommodity}>
            <SelectTrigger className="w-36 h-8 rounded-none border-zinc-800 bg-black text-xs font-mono">
              <SelectValue placeholder="All Commodities" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800 rounded-none">
              <SelectItem value="all" className="text-xs font-mono">All Commodities</SelectItem>
              {commodities.map(c => (
                <SelectItem key={c} value={c} className="text-xs font-mono">{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Trade Flow Table */}
      <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
        <CardHeader className="pb-2 px-4 pt-4">
          <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
            <Ship className="w-3.5 h-3.5 text-[#0ea5e9]" />
            Active Trade Corridors
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="h-[280px]">
            <table className="w-full">
              <thead className="sticky top-0 bg-zinc-950">
                <tr className="border-b border-zinc-800">
                  <th className="text-left text-[10px] text-zinc-500 uppercase font-mono px-4 py-2">Route</th>
                  <th className="text-left text-[10px] text-zinc-500 uppercase font-mono px-4 py-2">Commodity</th>
                  <th className="text-right text-[10px] text-zinc-500 uppercase font-mono px-4 py-2">Volume (MT)</th>
                  <th className="text-right text-[10px] text-zinc-500 uppercase font-mono px-4 py-2">Value ($B)</th>
                  <th className="text-right text-[10px] text-zinc-500 uppercase font-mono px-4 py-2">Change</th>
                </tr>
              </thead>
              <tbody>
                {filteredFlows.map((flow, i) => (
                  <tr key={i} className="border-b border-zinc-900 hover:bg-zinc-900/50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-zinc-300 font-mono">{flow.from}</span>
                        <ArrowRight className="w-3 h-3 text-zinc-600" />
                        <span className="text-xs text-zinc-300 font-mono">{flow.to}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className="rounded-none text-[9px] border-zinc-700 font-mono">
                        {flow.commodity}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-xs text-zinc-300 font-mono">{flow.volume}M</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-xs text-[#00ff41] font-mono">${flow.value}B</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`text-xs font-mono ${flow.change >= 0 ? "text-[#00ff41]" : "text-[#ff3b30]"}`}>
                        {flow.change >= 0 ? "+" : ""}{flow.change}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollArea>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Volume Chart */}
        <Card className="lg:col-span-2 bg-zinc-950/50 border-zinc-800 rounded-none">
          <CardHeader className="pb-2 px-4 pt-4">
            <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
              <Package className="w-3.5 h-3.5 text-[#eab308]" />
              Global Trade Volumes (Million MT)
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={COMMODITY_VOLUMES} layout="vertical">
                <XAxis type="number" tick={{ fill: "#71717a", fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" tick={{ fill: "#71717a", fontSize: 10 }} axisLine={false} tickLine={false} width={80} />
                <RechartsTooltip 
                  contentStyle={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 0, fontSize: 11, fontFamily: "JetBrains Mono" }} 
                  formatter={(v) => [`${v}M MT`, "Volume"]}
                />
                <Bar dataKey="volume" fill="#00ff41" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Top Exporters & Importers */}
        <div className="space-y-4">
          {/* Top Exporters */}
          <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                <TrendingUp className="w-3.5 h-3.5 text-[#00ff41]" />
                Top Exporters
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-2">
              {TOP_EXPORTERS.map((e, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b border-zinc-900">
                  <div>
                    <p className="text-xs text-zinc-300 font-mono">{e.country}</p>
                    <p className="text-[9px] text-zinc-600 font-mono">{e.commodities.join(", ")}</p>
                  </div>
                  <span className="text-xs font-mono text-[#00ff41]">${e.value}B</span>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Top Importers */}
          <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                <Globe className="w-3.5 h-3.5 text-[#0ea5e9]" />
                Top Importers
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-2">
              {TOP_IMPORTERS.map((e, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b border-zinc-900">
                  <div>
                    <p className="text-xs text-zinc-300 font-mono">{e.country}</p>
                    <p className="text-[9px] text-zinc-600 font-mono">{e.commodities.join(", ")}</p>
                  </div>
                  <span className="text-xs font-mono text-[#0ea5e9]">${e.value}B</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
